/**
 * The cells every ranked row shares: the formula's metric values (colored by
 * family, "—" when the range had no row, a dotted amber underline when a
 * later range's value rests on fewer visits than "Not enough data" needs), the score with
 * a bar against the rows shown, and the open/close chevron.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import { scoreLabel } from '../../../../general/hour/hour-chart-math';
import { valueTone } from '../../../../general/hour/values/value-tones';
import { columnText, columnValue, type RankingColumn } from '../ranking-columns';

/** "Not enough data" reads the first range only; a later range's value can rest on a handful of visits. */
export interface CellFloor {
  /** The default rule's number (Unique visits < N); null = no such rule, no underline. */
  minVolume: number | null;
  primaryRangeKey: string;
}

function thinSample(col: RankingColumn, metricsByRange: Record<string, Record<string, number>>, floor: CellFloor): string | null {
  if (floor.minVolume === null || col.rangeKey === floor.primaryRangeKey) return null;
  const uv = metricsByRange[col.rangeKey]?.uniqueVisits ?? 0;
  if (uv >= floor.minVolume) return null;
  return `${uv.toLocaleString('en-US')} unique visit${uv === 1 ? '' : 's'} in ${col.rangeLabel ?? 'this range'} — under the ${floor.minVolume.toLocaleString('en-US')} "Not enough data" needs, a thin sample (that rule reads the first range only)`;
}

export const MetricCells: FC<{ columns: RankingColumn[]; metricsByRange: Record<string, Record<string, number>>; floor: CellFloor }> = ({ columns, metricsByRange, floor }) => (
  <>
    {columns.map((col) => {
      const v = columnValue(col, metricsByRange);
      const thin = v === null ? null : thinSample(col, metricsByRange, floor);
      return (
        <span
          key={col.key}
          title={thin ?? undefined}
          className={cn(
            'hidden text-right font-mono text-xs tabular-nums sm:block',
            v === null ? 'text-muted-foreground/60' : valueTone(col.category, col.metric, v),
            thin && 'underline decoration-amber-500 decoration-dotted decoration-2 underline-offset-4',
          )}
        >
          {columnText(col, v)}
        </span>
      );
    })}
  </>
);

export const ScoreCell: FC<{ score: number | null; bar: number }> = ({ score, bar }) => (
  <div className="flex flex-col items-end gap-1">
    <span className={cn('font-mono tabular-nums', score === null ? 'text-[11px] text-muted-foreground' : 'text-sm font-semibold')}>
      {score === null ? 'unranked' : scoreLabel(score)}
    </span>
    {score === null ? null : (
      <span className="h-1 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        <span className="block h-full rounded-full bg-primary/70" style={{ width: `${bar}%` }} />
      </span>
    )}
  </div>
);

export const ExpandButton: FC<{ open: boolean; onClick: () => void; what: string }> = ({ open, onClick, what }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={open}
    aria-label={open ? `Hide how ${what} is scored` : `Show how ${what} is scored`}
    title={open ? 'Hide the math' : 'How the score is made · all values'}
    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
  >
    <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
  </button>
);
